import { useEffect, useMemo, useState } from 'react';
import { gridSizeMdLg12, TABLE_CONSTANTS, useTableFilter } from "@/shared";
import { GeneralTable } from "@/shared/components/ui";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import useTableColumnsTicket from "../hook/useTableColumnsTicket";

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import { TiTicket } from "react-icons/ti";
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { ROUTER_PATHS } from '@/shared/constants/routes/router-paths.constants';
import {
  useFetchGestionTickets,
  useFetchTicketEstados,
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  useFetchUsuarioResumen,
  useExportarTicketsExcel,
} from "@/shared/actions/erp/tickets-soporte-cliente";
import { useBulkReabrirTickets } from "@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-bulk.actions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import ContadoresEstado from './ContadoresEstado';
import ModalBulkAsignar from './ModalBulkAsignar';
import ModalBulkEscalar from './ModalBulkEscalar';
import DialogBulkProgress from './DialogBulkProgress';
import useTicketBulkProgress from '../hook/useTicketBulkProgress';
import FormTicketInterno from '../../tickets-internos/components/FormTicketInterno';

type ViewTicketPageProps = {
  title: string;
}

export default function ViewTicketPage({ title }: ViewTicketPageProps) {
  const [searchParams] = useSearchParams();
  const tipoFiltro = searchParams.get('tipo');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Permisos del usuario
  const user = useAuthStore((s) => s.user);
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const isSuperUser = !!(user as any)?.is_superuser;
  const { data: resumenPermsRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _rp = resumenPermsRes as any;
  const userPermisos: string[] = _rp?.data?.data?.permisos || _rp?.data?.permisos || _rp?.permisos || [];
  const canBulkAsignar = isSuperUser || userPermisos.includes('tickets.asignar_ticket');
  const canBulkEscalar = isSuperUser || userPermisos.includes('tickets.escalar_ticket');
  const canBulkReabrir = isSuperUser || userPermisos.includes('tickets.reabrir_ticket');
  const canTicketInterno = isSuperUser || userPermisos.includes('tickets.crear_ticket_interno');
  const canExportarExcel = isSuperUser || userPermisos.includes('tickets.exportar_excel');
  const canVerTicketDetalle = isSuperUser || userPermisos.includes('tickets.ver_ticket_detalle');
  const canEditarTicket = isSuperUser || userPermisos.includes('tickets.editar_ticket');
  // Boton "Dashboard Supervisor" gated por permiso configurable desde
  // /parametrizacion/gestion-accesos > Tickets. Solo aparece en la vista
  // "tipo=departamento" porque es donde un supervisor opera sobre el equipo.
  const canVerDashboardSupervisor = isSuperUser || userPermisos.includes('tickets.ver_dashboard_supervisor');
  const showDashboardBtn = tipoFiltro === 'departamento' && canVerDashboardSupervisor;

  const bulkReabrirMutation = useBulkReabrirTickets();
  const exportarExcelMutation = useExportarTicketsExcel();
  const [openCrearInterno, setOpenCrearInterno] = useState(false);

  // Filtros (selects encima de la tabla)
  const [estadoFilter, setEstadoFilter] = useState('');
  const [prioridadFilter, setPrioridadFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Filtros de columna (subheader del MaterialReactTable). Vienen como
  // [{id: accessorKey, value: 'texto'}]. Se mapean a query params del
  // backend para que el filtrado sea server-side y aplique a TODAS las
  // paginas, no solo a la visible.
  const [columnFilters, setColumnFilters] = useState<Array<{ id: string; value: any }>>([]);

  // Buscador GLOBAL (lupa "Mostrar/ocultar busqueda" del MRT). Se envia al
  // backend como `search` y alli se hace OR (LIKE) sobre codigo, cliente,
  // descripcion y usuarios — server-side, cubre TODAS las paginas.
  const [globalFilter, setGlobalFilter] = useState('');

  // Row selection (keyed by UUID)
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Bulk modals state
  const [openBulkAsignar, setOpenBulkAsignar] = useState(false);
  const [openBulkEscalar, setOpenBulkEscalar] = useState(false);
  const [openBulkProgress, setOpenBulkProgress] = useState(false);
  const [bulkOperacionId, setBulkOperacionId] = useState<string | null>(null);

  // Bulk progress polling
  const { progress: bulkProgress, isPolling: isBulkPolling, reset: resetBulkProgress } = useTicketBulkProgress({
    operacionId: bulkOperacionId,
    onComplete: () => {
      // refetchQueries fuerza el GET inmediato de la lista, los contadores
      // (ContadoresEstado / useFetchTicketResumen), el detalle, etc.
      // invalidateQueries por si solo solo marca stale y puede no refetchear
      // a tiempo cuando el staleTime global es 30s.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
    },
  });

  // Derive selected ticket IDs from rowSelection
  const selectedTicketIds = useMemo(
    () => Object.keys(rowSelection).filter(k => rowSelection[k]),
    [rowSelection]
  );

  // Catalogos para filtros.
  // erpApi.get() ya unwrappea axios.data, asi que `dataEstados` ES el body
  // del response: { code, status, data: [...], total, page, page_size, pages }.
  // El array vive en `dataEstados.data` directamente, sin nivel extra.
  const { data: dataEstados } = useFetchTicketEstados({ page_size: 50 });
  const { data: dataPrioridades } = useFetchTicketPrioridades({ page_size: 50 });
  const { data: dataAreas } = useFetchTicketAreas({ page_size: 50 });

  const estados = (dataEstados as any)?.data || [];
  const prioridades = (dataPrioridades as any)?.data || [];
  const areas = (dataAreas as any)?.data || [];

  const { columnsTicket } = useTableColumnsTicket();

  const {
    pagination,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  // Mapeo accessorKey (columna del MRT) -> query param que entiende el
  // backend /api/v1/gestion-tickets/tickets. Es el "lenguaje" comun entre
  // los filtros de columna (texto libre) y el spec server-side.
  const COLUMN_FILTER_MAP: Record<string, string> = {
    'codigo_ticket': 'codigo_ticket',
    'estado_ticket_data.nombre': 'estado_ticket_nombre',
    'prioridad_data.nombre': 'prioridad_nombre',
    'tipo_data.nombre': 'tipo_nombre',
    'cliente_razon_social': 'cliente_razon_social',
    'area_origen_data.nombre': 'area_origen_nombre',
    'area_destino_data.nombre': 'area_destino_nombre',
    'canal_data.nombre': 'canal_nombre',
    'usuario_creador_nombre': 'usuario_creador_nombre',
    'usuario_asignado_nombre': 'usuario_asignado_nombre',
    'descripcion': 'descripcion',
  };

  // Construir params. El scope se infiere del query string `tipo`:
  //   tipo=individual  -> solo tickets asignados al usuario actual
  //   tipo=departamento -> todos los tickets (no filtra por usuario; cuando
  //                       el profile incluya area_id se sumara area_destino)
  // El resto de filtros (estado, prioridad, area, fechas, columnas) son
  // aditivos y se aplican server-side via el endpoint /tickets/.
  const queryParams: Record<string, any> = {
    page: pageIndex + 1,
    page_size: pageSize,
  };
  if (tipoFiltro === 'individual' && currentUserId) {
    queryParams.usuario_asignado_id = currentUserId;
  }
  if (estadoFilter) queryParams.estado_ticket = estadoFilter;
  if (prioridadFilter) queryParams.prioridad = prioridadFilter;
  if (areaFilter) queryParams.area_destino = areaFilter;
  if (fechaDesde) queryParams.fecha_creacion_after = fechaDesde;
  if (fechaHasta) queryParams.fecha_creacion_before = fechaHasta;
  // Filtros de columna del MRT. Cada uno pisa al de los selects si coincide
  // el query param, lo cual es OK porque el ultimo gana (server-side).
  for (const cf of columnFilters) {
    const param = COLUMN_FILTER_MAP[cf.id];
    if (!param) continue;
    const val = typeof cf.value === 'string' ? cf.value.trim() : cf.value;
    if (val === undefined || val === null || val === '') continue;
    queryParams[param] = val;
  }
  // Buscador global (lupa). Un solo texto -> `search` (OR server-side).
  if (typeof globalFilter === 'string' && globalFilter.trim()) {
    queryParams.search = globalFilter.trim();
  }

  const {
    data,
    isLoading,
    isRefetching
  } = useFetchGestionTickets(queryParams);

  // Serializamos los columnFilters para dispararlo en el effect: si lo
  // pasaramos como array crudo, la referencia cambia en cada render y el
  // effect entra en loop. Con string solo cambia cuando el contenido cambia.
  const columnFiltersKey = JSON.stringify(columnFilters);

  // Reset a pagina 1 cuando cambian los filtros. Sin esto, si estabas en
  // pagina 5 y aplicas un filtro que reduce los resultados a 2 paginas, el
  // servidor recibe page=5 y devuelve vacio, dando la impresion de que el
  // filtro no funciona.
  useEffect(() => {
    if (pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoFilter, prioridadFilter, areaFilter, fechaDesde, fechaHasta, tipoFiltro, columnFiltersKey, globalFilter]);

  const handleClearFilters = () => {
    setEstadoFilter('');
    setPrioridadFilter('');
    setAreaFilter('');
    setFechaDesde('');
    setFechaHasta('');
    setColumnFilters([]);
  };

  const handleEstadoFromContador = (codigo: string) => {
    // Buscar el ID del estado por codigo
    if (!codigo) {
      setEstadoFilter('');
      return;
    }
    const est = estados.find((e: any) => e.codigo === codigo);
    if (est) setEstadoFilter(est.id_ticket_estado);
  };

  const onViewDetail = (row: any) => {
    const ticketId = row.id_ticket;
    if (ticketId) {
      navigate(`/tickets-soporte-cliente/detalle-ticket/${ticketId}`);
    }
  };

  const onEditTicket = (row: any) => {
    const ticketId = row.id_ticket;
    if (ticketId) {
      navigate(`/tickets-soporte-cliente/editar-ticket/${ticketId}`);
    }
  };

  const handleBulkSuccess = (operacionId: string) => {
    setBulkOperacionId(operacionId);
    setOpenBulkProgress(true);
  };

  const handleCloseBulkProgress = () => {
    setOpenBulkProgress(false);
    setBulkOperacionId(null);
    resetBulkProgress();
    setRowSelection({});
  };

  // Obtener el codigo del estado activo para el contador
  const estadoActivoCodigo = estados.find((e: any) => e.id_ticket_estado === estadoFilter)?.codigo || '';

  return (
    <>
      <Paper elevation={2} sx={{ padding: 2, mb: 2, borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h4"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TiTicket />
            {tipoFiltro === 'departamento'
              ? 'TICKETS POR DEPARTAMENTO'
              : tipoFiltro === 'individual'
                ? 'MIS TICKETS'
                : title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {selectedTicketIds.length > 0 && (
              <>
                {canBulkAsignar && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => setOpenBulkAsignar(true)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Asignar Masiva ({selectedTicketIds.length})
                  </Button>
                )}
                {canBulkEscalar && (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<TrendingUpIcon />}
                    onClick={() => setOpenBulkEscalar(true)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Escalar Masiva ({selectedTicketIds.length})
                  </Button>
                )}
                {canBulkReabrir && (
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<ReplayIcon />}
                    onClick={() => {
                      bulkReabrirMutation.mutate(
                        { ticket_ids: selectedTicketIds },
                        {
                          onSuccess: (res) => {
                            const opId = (res as any)?.data?.data?.operacion_id;
                            if (opId) {
                              setBulkOperacionId(opId);
                              setOpenBulkProgress(true);
                            }
                            setRowSelection({});
                          },
                        }
                      );
                    }}
                    disabled={bulkReabrirMutation.isPending}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    {bulkReabrirMutation.isPending ? 'Reabriendo...' : `Reabrir Masiva (${selectedTicketIds.length})`}
                  </Button>
                )}
              </>
            )}
            {canExportarExcel && (
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<FileDownloadIcon />}
              disabled={exportarExcelMutation.isPending}
              onClick={() => {
                // Si hay filas seleccionadas, exporta solo esas. Si no,
                // exporta el conjunto resultante de aplicar los filtros
                // actuales (sin paginar — el endpoint trae todo lo que
                // matchea).
                const ids = selectedTicketIds.length > 0 ? selectedTicketIds : undefined;
                const params: any = {};
                if (tipoFiltro === 'individual' && currentUserId) {
                  params.usuario_asignado_id = currentUserId;
                }
                if (estadoFilter) params.estado_ticket = estadoFilter;
                if (prioridadFilter) params.prioridad = prioridadFilter;
                if (areaFilter) params.area_destino = areaFilter;
                if (fechaDesde) params.fecha_creacion_after = fechaDesde;
                if (fechaHasta) params.fecha_creacion_before = fechaHasta;
                if (ids) params.ids = ids;
                exportarExcelMutation.mutate(params);
              }}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {exportarExcelMutation.isPending
                ? 'Exportando...'
                : selectedTicketIds.length > 0
                  ? `Exportar Excel (${selectedTicketIds.length})`
                  : 'Exportar Excel'}
            </Button>
            )}
            {showDashboardBtn && (
              <Tooltip title="Ver KPIs en tiempo real, ranking de asesores y descargar reporte XLSX">
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate(ROUTER_PATHS.ticketSoporteCliente.dashboard_nav)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Dashboard Supervisor
                </Button>
              </Tooltip>
            )}
            {canTicketInterno && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenCrearInterno(true)}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                Ticket Interno
              </Button>
            )}
          </Box>
        </Box>

        {/* Contadores por estado.
            - tipo=individual (Mis Tickets) -> filtra por usuario_asignado.
            - tipo=departamento -> el backend resuelve el scope sumando las
              areas del usuario autenticado (un usuario puede pertenecer a N
              areas; todas se incluyen). No se hardcodea un area aqui porque
              los contadores deben ser coherentes con el listado, no con la
              "primera area" del perfil. */}
        <ContadoresEstado
          usuarioAsignado={tipoFiltro === 'individual' && currentUserId ? String(currentUserId) : undefined}
          onEstadoClick={handleEstadoFromContador}
          estadoActivo={estadoActivoCodigo}
        />

        {/* Filtros */}
        <Box sx={{ mb: 2, mt: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  label="Estado"
                  onChange={(e: SelectChangeEvent) => setEstadoFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estados.map((e: any) => (
                    <MenuItem key={e.id_ticket_estado} value={e.id_ticket_estado}>
                      <Chip
                        label={e.nombre}
                        size="small"
                        sx={{ bgcolor: e.color, color: '#fff', mr: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioridad</InputLabel>
                <Select
                  value={prioridadFilter}
                  label="Prioridad"
                  onChange={(e: SelectChangeEvent) => setPrioridadFilter(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {prioridades.map((p: any) => (
                    <MenuItem key={p.id_ticket_prioridad} value={p.id_ticket_prioridad}>
                      <Chip
                        label={p.nombre}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: p.color, color: p.color, mr: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Area</InputLabel>
                <Select
                  value={areaFilter}
                  label="Area"
                  onChange={(e: SelectChangeEvent) => setAreaFilter(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {areas.map((a: any) => (
                    <MenuItem key={a.id_area} value={a.id_area}>
                      {a.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Desde"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Hasta"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Limpiar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tabla */}
        <Grid item {...gridSizeMdLg12}>
          <GeneralTable
            columns={columnsTicket}
            data={(data as any)?.data || []}
            isLoading={isLoading}
            isRefetching={isRefetching}
            pagination={pagination}
            onPaging={setPagination}
            canEdit={false}
            canDelete={false}
            // Filtros de columna server-side: el subheader del MRT envia
            // cada input al backend via COLUMN_FILTER_MAP y se aplica un
            // LIKE en el spec, asi el filtrado cubre TODAS las paginas y no
            // solo la visible. Sin manualFiltering, MRT filtraria localmente
            // las ~10 filas que ya descargo, dando la impresion de que no
            // funciona cuando hay miles de tickets.
            enableColumnFilters={true}
            enableManualFiltering={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters as any}
            // Buscador global (lupa): el texto viaja al backend como `search`
            // (server-side, todas las paginas). MRT lo debouncea internamente.
            onGlobalFilterChange={setGlobalFilter}
            rowCount={(data as any)?.total}
            actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
            showCustomActionBtnSpace={true}
            enableRowSelection={true}
            enableSelectAll={true}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            getRowId={(row: any) => row.id_ticket}
            customActionBtnSpace={v => {
              return (
                <Grid container>
                  {canVerTicketDetalle && (
                    <Grid item>
                      <Tooltip title="Ver Detalle">
                        <IconButton onClick={() => onViewDetail(v)}>
                          <RemoveRedEyeIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                  {canEditarTicket && (
                    <Grid item>
                      <Tooltip title="Editar Ticket">
                        <IconButton onClick={() => onEditTicket(v)}>
                          <EditIcon color="warning" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              );
            }}
          />
        </Grid>
      </Paper>

      {/* Bulk Modals */}
      <ModalBulkAsignar
        open={openBulkAsignar}
        onClose={() => setOpenBulkAsignar(false)}
        ticketIds={selectedTicketIds}
        onSuccess={handleBulkSuccess}
      />
      <ModalBulkEscalar
        open={openBulkEscalar}
        onClose={() => setOpenBulkEscalar(false)}
        ticketIds={selectedTicketIds}
        onSuccess={handleBulkSuccess}
      />
      <DialogBulkProgress
        open={openBulkProgress}
        onClose={handleCloseBulkProgress}
        progress={bulkProgress}
        isPolling={isBulkPolling}
      />

      {/* Modal Ticket Interno */}
      <FormTicketInterno
        open={openCrearInterno}
        onClose={() => setOpenCrearInterno(false)}
      />
    </>
  );
}
